<template>
  <form v-if="sourceInfo">
    <v-text-field
      v-model="sourceInfo.name"
      label="Name"
      :loading="state.siteListLoading || state.saveLoading"
    ></v-text-field>
    <v-autocomplete
      :items="state.siteList"
      v-model="state.selectedSite"
      :loading="state.siteListLoading || state.saveLoading"
      placeholder="Select Site Urls"
      label="Site Urls"
    ></v-autocomplete>

    <div class="align-center d-flex justify-end flex-column-reverse flex-sm-row flex-wrap">
      <v-btn
        :loading="state.loading"
        class="mt-4 mt-sm-0 w-100 w-sm-auto"
        variant="elevated"
        color="grey"
        :to="URLs.PAGE_CUSTOMER_EDIT(customerId)"
        :disabled="state.saveLoading"
        text="Cancel"
      />
      <v-btn
        :loading="state.saveLoading"
        @click="handleClickSave"
        class="ml-0 ml-sm-2 w-100 w-sm-auto"
        color="primary"
        variant="elevated"
        append-icon="mdi-content-save"
        text="Save"
      />
    </div>
  </form>
  <Loading message="Loading source info..." v-else />
</template>

<script setup lang="ts">
const props = defineProps<{
  accountId: string;
}>();

import { useRouter } from "vue-router";
import { onMounted, ref, watch } from "vue";
import { IApp } from "@/types";
import { useAccountStore } from "@/stores/account";
import { makeAlert } from "@/utils";
import GoogleSearchConsoleAction from "@/actions/GoogleSearchConsole";
import { URLs } from "@/constants";
import Loading from "../Widgets/Loading.vue";
import _ from "lodash";
import useRouteParams from "@/hooks/useRouteParams";
import { useAuthStore } from "@/stores/auth";
import { computed } from "vue";
const router = useRouter();
const accountStore = useAccountStore();
const authStore = useAuthStore();
const user = computed(() => authStore.user);

const { sourceId, customerId } = useRouteParams();
const sourceInfo = ref<IApp.IGoogleSearchConsoleAccount>();

const state = ref<{
  loading: boolean;
  siteListLoading: boolean;
  saveLoading: boolean;
  siteList: string[];
  selectedSite: string | null;
}>({
  loading: false,
  siteListLoading: false,
  saveLoading: false,
  siteList: [],
  selectedSite: null
});

const fetchAccountInfo = async (customerId: string | undefined, sourceId: string | undefined) => {
  if (sourceId && customerId) {
    state.value.loading = true;
    const response = await accountStore.fetchAccountInfo<IApp.IFeedTypes.GoogleSearchConsole>(
      user.value?.id!,
      customerId,
      sourceId
    );
    if (response.status) {
      if (response.data.account.feed === IApp.IFeedTypes.GoogleSearchConsole) {
        sourceInfo.value = response.data.account;
        state.value.selectedSite = _.head(response.data.account.data.siteUrls) ?? null;
      } else {
        makeAlert("This account is not a Google Search Console account", "success");
        state.value.loading = false;
        router.push({
          name: "profile"
        });
      }
    } else {
      makeAlert(response.message, "error");
    }
    state.value.loading = false;
  }
};

const fetchSiteList = async (customerId: string | undefined, sourceId: string | undefined) => {
  if (sourceId && customerId) {
    state.value.siteListLoading = true;
    const response = await GoogleSearchConsoleAction.getSiteList(
      user.value?.id!,
      customerId,
      sourceId
    );
    if (response.status) {
      state.value.siteList = response.data.siteUrls;
    } else {
      makeAlert(response.message, "error");
    }
    state.value.siteListLoading = false;
  }
};

const handleClickSave = async () => {
  if (!state.value.selectedSite) {
    makeAlert("Please select a site url", "error");
  } else if (sourceInfo.value && confirm("Are you sure you want to save?")) {
    state.value.saveLoading = true;
    const response = await accountStore.patchAccountInfo(
      user.value?.id!,
      customerId.value!,
      sourceInfo.value.account,
      {
        type: IApp.IFeedTypes.GoogleSearchConsole,
        name: sourceInfo.value.name,
        siteUrls: [state.value.selectedSite]
      }
    );
    if (response.status) {
      makeAlert(response.data.message, "success");
    } else {
      makeAlert(response.message, "error");
    }
    state.value.saveLoading = false;
  }
};

onMounted(() => fetchAccountInfo(customerId.value, sourceId.value));

onMounted(() => fetchSiteList(customerId.value, sourceId.value));
</script>
